import { useField, FieldArray } from 'formik';
import { useTheme, Typography, useMediaQuery } from '@mui/material';

import { AddButton, InputField, SwitchField } from 'components';

import { FormSelectFieldProps } from './types';
import { OtherOptionRow } from './other-option-row';
import { SelectOptionRow } from './select-option-row';
import { getAlignedAnswerValues } from './answer-values';
import { RandomizeOptionsField } from './randomize-options';

export const FormSelectField = ({
  name,
  allowReferenceCodes = false,
}: FormSelectFieldProps) => {
  const [, , { setValue: setAllowOther }] = useField<boolean | undefined>(
    `${name}.options.allowOther`
  );
  const [, , { setValue: setOtherAnswerValue }] = useField<string | undefined>(
    `${name}.options.otherAnswerValue`
  );
  const [, , { setValue: setExplanation }] = useField(
    `${name}.options.explanation`
  );
  const [, { value: items = [] }] = useField<string[]>(
    `${name}.options.options`
  );
  const [, { value: answerValuesValue }, { setValue: setAnswerValuesValue }] =
    useField<string[] | undefined>(`${name}.options.answerValues`);
  const answerValues = Array.isArray(answerValuesValue)
    ? answerValuesValue
    : [];
  const alignedAnswerValues = getAlignedAnswerValues(
    answerValues,
    items.length
  );
  const [
    ,
    { value: correctOptionsValue },
    { setValue: setCorrectOptionsValue },
  ] = useField<number[] | undefined>(`${name}.options.correctOptions`);
  const correctOptions = Array.isArray(correctOptionsValue)
    ? correctOptionsValue
    : [];
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('md'));
  const isCorrectOptionsEnabled = Array.isArray(correctOptionsValue);

  return (
    <div className="flex flex-col gap-4">
      <RandomizeOptionsField name={name} />
      <SwitchField label="Multiple" name={`${name}.options.multiple`} />
      <OtherOptionRow
        name={name}
        allowReferenceCodes={allowReferenceCodes}
        onChange={(_, checked) => {
          if (checked) {
            setCorrectOptionsValue(undefined);
            setExplanation(undefined);
          }
        }}
      />

      <SwitchField
        label="Correct options"
        name={`${name}.options.correctOptions`}
        formatRenderValue={(value) => Array.isArray(value)}
        formatFieldValue={(value) => (value ? [] : undefined)}
        onChange={(e) => {
          if (e.target.checked) {
            setAllowOther(false);
            // Correct-options mode disables Other without firing its switch handler.
            setOtherAnswerValue(undefined);
            setExplanation('');
          } else {
            setExplanation(undefined);
          }
        }}
      />

      {isCorrectOptionsEnabled && (
        <InputField
          label="Explanation"
          placeholder="Explanation"
          name={`${name}.options.explanation`}
        />
      )}

      <FieldArray
        name={`${name}.options.options`}
        render={({ push, remove }) => (
          <div className="flex flex-col gap-4">
            <Typography variant="h6">Select Options</Typography>

            {items.map((_, idx) => (
              <SelectOptionRow
                idx={idx}
                key={idx}
                name={name}
                remove={remove}
                isTablet={isTablet}
                itemsLength={items.length}
                correctOptions={correctOptions}
                answerValues={alignedAnswerValues}
                allowReferenceCodes={allowReferenceCodes}
                setAnswerValuesValue={setAnswerValuesValue}
                setCorrectOptionsValue={setCorrectOptionsValue}
                isCorrectOptionsEnabled={isCorrectOptionsEnabled}
              />
            ))}

            <AddButton
              title="option"
              onClick={() => {
                push('');
                setAnswerValuesValue([...alignedAnswerValues, ''], false);
              }}
            />
          </div>
        )}
      />
    </div>
  );
};
